/**
 * The garden's seed order address (D69; design rev 1 §1.1 and §4.3, critique R2, R21): the one-line how-it-works, the
 * address in one box (it breaks after the @ on a phone, never a horizontal scroll), Copy full width under it ("Copied"
 * beside it for a moment), and New address as a quiet text button for the garden's owner, which asks first ("Your old
 * address stops working at once…"). On Free the server makes no address: the Grow More sentence and Subscribe instead;
 * a lapsed trial keeps the address it had, shown with the same sentence under it.
 *
 * The how-it-works sentence ("Forward a seed order to this address...") shows only when the server sent an address (B112):
 * with none, "this address" would point at nothing, so the card draws only what else it has (the Grow More sentence on Free).
 *
 * The server decides all of it (GET /seed-orders/mine: allowed, address, isOwner); this draws it. A failure on our side
 * says the standard sentence (reported, hello@gardengameplan.com), never "try again".
 *
 * Where: SeedOrdersScreen (Profile > Seed orders by email) and the Seed vault's empty state (LocationCard).
 * API: GET /seed-orders/mine, POST /seed-orders/address/new (api/seedOrders). Words (en/es): profile.seeds.*.
 *
 * @module SeedAddressCard
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { newSeedAddress, type SeedOrdersMine } from '@/shared/api/seedOrders';
import { SEED_ORDERS_MINE_KEY } from './lib/seedOrderQueries';

/** How long "Copied" shows, in milliseconds. @kept K5 */
const COPIED_MS = 2000;

/** What the card is given. */
interface SeedAddressCardProps {
  /** The server's answer (GET /seed-orders/mine). */
  mine: SeedOrdersMine;
  /** Leave out the how-it-works line (the profile screen says it above). It is also left out when there is no address. */
  hideAbout?: boolean;
}

/**
 * The address, Copy and New address; or the Grow More sentence on Free.
 *
 * @param props - SeedAddressCardProps.
 */
export const SeedAddressCard: React.FC<SeedAddressCardProps> = ({ mine, hideAbout = false }) => {
  const { reactTranslate: t } = useTranslation();
  const queryClient = useQueryClient();
  const [copied, setCopied] = React.useState(false);
  const [asking, setAsking] = React.useState(false);
  const [failed, setFailed] = React.useState<string | null>(null);
  const timer = React.useRef<number | undefined>(undefined);
  React.useEffect(() => () => window.clearTimeout(timer.current), []);

  /** "New address": the answer replaces the address shown everywhere. */
  const renew = useMutation({
    mutationFn: newSeedAddress,
    onMutate: () => setFailed(null),
    onSuccess: (address) => {
      queryClient.setQueryData(SEED_ORDERS_MINE_KEY, { ...mine, address });
      setAsking(false);
    },
    onError: (error) => { setFailed(errorMessage(error, t('profile.seeds.newFailed'))); setAsking(false); },
  });

  /** Copies the whole address and says so for a moment. */
  const copy = async (): Promise<void> => {
    if (!mine.address) return;
    try {
      await navigator.clipboard.writeText(mine.address);
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), COPIED_MS);
    } catch {
      // The clipboard refused (an old browser, no permission): the address is on screen to select by hand.
    }
  };

  return (
    <div className="space-y-3">
      {!hideAbout && mine.address && <p className="text-sm">{t('profile.seeds.about')}</p>}
      {mine.address && (
        <>
          <div
            className="break-words rounded-md border bg-muted px-3 py-2.5 font-mono text-[0.9375rem] leading-snug"
            aria-label={t('profile.seeds.addressLabel')}
          >
            {/* It breaks after the @ when it does not fit on one line (a word longer than the box breaks anywhere). */}
            {mine.address.slice(0, mine.address.indexOf('@') + 1)}<wbr />{mine.address.slice(mine.address.indexOf('@') + 1)}
          </div>
          <Button type="button" className="h-11 w-full gap-2" onClick={() => void copy()}>
            <Copy className="h-4 w-4" aria-hidden="true" />
            {copied ? t('profile.seeds.copied') : t('profile.seeds.copy')}
          </Button>
          {mine.isOwner && mine.allowed && (
            <button
              type="button"
              className="min-h-11 px-1 text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
              onClick={() => setAsking(true)}
            >
              {t('profile.seeds.newAddress')}
            </button>
          )}
        </>
      )}
      {!mine.allowed && (
        <div className="space-y-2">
          <p className="text-sm">{t('profile.seeds.growMore')}</p>
          <Button asChild className="h-11 w-full">
            <Link to="/subscribe">{t('profile.seeds.subscribe')}</Link>
          </Button>
        </div>
      )}
      {failed && <p role="alert" className="text-sm text-destructive"><FailureText text={failed} /></p>}
      <BottomPanel open={asking} onOpenChange={setAsking} title={t('profile.seeds.newTitle')} wrapTitle>
        <p className="text-sm">{t('profile.seeds.newBody')}</p>
        <div className="mt-3">
          <ActionRow cancelLabel={t('profile.seeds.keepIt')} disabled={renew.isPending} onCancel={() => setAsking(false)}>
            <Button type="button" busy="saving" loading={renew.isPending} onClick={() => renew.mutate()}>
              {t('profile.seeds.changeIt')}
            </Button>
          </ActionRow>
        </div>
      </BottomPanel>
    </div>
  );
};
